import React, { useEffect, useState } from "react";
import { Modal, Badge } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import styles from "./TempleFilterPopup.module.css";
import Inputs from "../Inputs/CustomizInput";
import { AxiosInstance } from "../../axios";
import CustomizButton from "../buttons/CustomizButton";

const TempleFilterPopup = ({
  show,
  onClose,
  onApply,
  onClear,
  locationState,
  filters,
  setFilters,
  emptyFilters,
}) => {
  const [tagOptions, setTagOptions] = useState([]);
  const [religionOptions, setReligionOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    // when parent gives a locationState, put it into filters.group
    if (locationState?.value && locationState?.label) {
      setFilters((prev) => ({
        ...prev,
        group: [{ value: locationState.value, label: locationState.label }],
      }));
    }
  }, [locationState]);

  // --- Fetch helpers (kept as separate functions) ---
  const safeMapToOption = (item) => {
    const option = {};
    if (item.id != null) option.value = item.id;
    else if (item.name && item.name.trim() !== "") option.value = item.name;
    else if (item.value != null) option.value = item.value;

    if (item.name && item.name.trim() !== "") option.label = item.name;
    else if (item.label && item.label.trim() !== "") option.label = item.label;
    else if (item.value != null) option.label = item.value;

    if (!option.value || !option.label) return null;
    return option;
  };

  const fetchTags = async () => {
    try {
      const res = await AxiosInstance.get("temple/tags");
      if (res.data?.status && Array.isArray(res.data.lists)) {
        setTagOptions(res.data.lists.map(safeMapToOption).filter(Boolean));
      }
    } catch (err) {
      console.error("fetchTags", err);
    }
  };

  const fetchReligions = async () => {
    try {
      const res = await AxiosInstance.get("temple/religion/0");
      if (res.data?.status && Array.isArray(res.data.lists)) {
        setReligionOptions(res.data.lists.map(safeMapToOption).filter(Boolean));
      }
    } catch (err) {
      console.error("fetchReligions", err);
    }
  };

  const fetchFacilities = async () => {
    try {
      const res = await AxiosInstance.get("temple/facilities/0");
      if (res.data?.status && Array.isArray(res.data.lists)) {
        const unique = res.data.lists
          .map(safeMapToOption)
          .filter(Boolean)
          .filter(
            (opt, i, arr) =>
              i ===
              arr.findIndex(
                (o) => o.label.toLowerCase() === opt.label.toLowerCase()
              )
          );
        setFacilityOptions(unique);
      }
    } catch (err) {
      console.error("fetchFacilities", err);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await AxiosInstance.get("temple/group_name/0");
      if (res.data?.status && Array.isArray(res.data.lists)) {
        const unique = res.data.lists
          .map(safeMapToOption)
          .filter(Boolean)
          .filter(
            (opt, i, arr) =>
              i ===
              arr.findIndex(
                (o) => o.label.toLowerCase() === opt.label.toLowerCase()
              )
          );
        setGroupOptions(unique);
      }
    } catch (err) {
      console.error("fetchGroups", err);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await AxiosInstance.get("temple/list_state");
      if (res.data?.status && Array.isArray(res.data.state)) {
        const uniqueOptions = res.data.state
          .map((s) =>
            s.state_name && s.state_name.trim() !== ""
              ? {
                  value: s.state_name.toLowerCase().replace(/\s+/g, "_"),
                  label: s.state_name,
                }
              : null
          )
          .filter(Boolean)
          .filter(
            (opt, i, arr) =>
              i ===
              arr.findIndex(
                (o) => o.label.toLowerCase() === opt.label.toLowerCase()
              )
          );
        setStateOptions(uniqueOptions);
      }
    } catch (err) {
      console.error("fetchStates", err);
    }
  };

  const fetchDistricts = async (stateObj) => {
    if (!stateObj) return;
    try {
      const res = await AxiosInstance.post("temple/list_district", {
        state_name: stateObj.label,
      });
      if (res.data?.status && Array.isArray(res.data.district)) {
        const options = res.data.district
          .map((d) =>
            d.district_name && d.district_name.trim() !== ""
              ? {
                  value: d.district_name.toLowerCase().replace(/\s+/g, "_"),
                  label: d.district_name,
                }
              : null
          )
          .filter(Boolean);
        setDistrictOptions(options);
        // reset dependent fields
        setCityOptions([]);
        setFilters((prev) => ({ ...prev, district: null, city: null }));
      }
    } catch (err) {
      console.error("fetchDistricts", err);
    }
  };

  const fetchCities = async (districtObj) => {
    if (!districtObj) return;
    try {
      const res = await AxiosInstance.post("temple/list_city", {
        district_name: districtObj.label,
      });
      if (res.data?.status && Array.isArray(res.data.city)) {
        const options = res.data.city
          .map((c) =>
            c.city_name && c.city_name.trim() !== ""
              ? {
                  value: c.city_name.toLowerCase().replace(/\s+/g, "_"),
                  label: c.city_name,
                }
              : null
          )
          .filter(Boolean);
        setCityOptions(options);
        setFilters((prev) => ({ ...prev, city: null }));
      } else {
        setCityOptions([]);
        setFilters((prev) => ({ ...prev, city: null }));
      }
    } catch (err) {
      console.error("fetchCities", err);
      setCityOptions([]);
      setFilters((prev) => ({ ...prev, city: null }));
    }
  };

  useEffect(() => {
    fetchTags();
    fetchReligions();
    fetchFacilities();
    fetchGroups();
    fetchStates();
  }, []);

  // when state changes, fetch districts
  useEffect(() => {
    if (filters.state) fetchDistricts(filters.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.state?.value, filters.state?.label]); // track state change

  useEffect(() => {
    if (filters.district) fetchCities(filters.district);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.district?.value, filters.district?.label]);

  // colors for chips
  const filterColors = {
    tag: "backgroundblue",
    religion: "backgroundpink",
    facilities: "backgroundpink",
    group: "backgroundpink",
    ownership: "backgroundpink",
    state: "backgroundrose",
    district: "backgroundrose",
    city: "backgroundrose",
  };

  const removeFilter = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (Array.isArray(next[field])) {
        next[field] = next[field].filter((i) => i.value !== value);
      } else {
        next[field] = null;
      }
      return next;
    });
  };

  const handleClear = () => {
    setFilters(emptyFilters);
    if (onClear) onClear(true);
  };

  const countApplied = () => {
    const f = filters;
    return (
      (f.tag ? 1 : 0) +
      (f.religion?.length || 0) +
      (f.facilities?.length || 0) +
      (f.group?.length || 0) +
      (f.ownership ? 1 : 0) +
      (f.state ? 1 : 0) +
      (f.district ? 1 : 0) +
      (f.city ? 1 : 0)
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      backdrop="static"
      keyboard={false}
      centered
      scrollable
      contentClassName={`${styles.modalContent} p-3`}
    >
      <Modal.Header closeButton>
        <Modal.Title className={styles.title}>Filters</Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        {/* Chips */}
        <div className="mb-4 d-flex flex-wrap gap-2">
          {Object.entries(filters).map(([field, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              return value.map((v) => (
                <Badge
                  key={`${field}-${v.value}`}
                  pill
                  className={`${styles.chip} ${filterColors[field] || ""}`}
                >
                  {v.label}{" "}
                  <X
                    size={14}
                    style={{ cursor: "pointer" }}
                    onClick={() => removeFilter(field, v.value)}
                  />
                </Badge>
              ));
            } else if (value && value.value) {
              return (
                <Badge
                  key={`${field}-${value.value}`}
                  pill
                  className={`${styles.chip} ${filterColors[field] || ""}`}
                >
                  {value.label}{" "}
                  <X
                    size={14}
                    style={{ cursor: "pointer" }}
                    onClick={() => removeFilter(field, value.value)}
                  />
                </Badge>
              );
            }
            return null;
          })}
        </div>

        {/* Tag */}
        <div className="col-12 px-2 mb-4">
          <div className="backgroundblue p-1 rounded-4">
            <h5 className="sub-title p-2 m-0">Tag</h5>
            <div className="row p-2 bg-white rounded-bottom-10">
              <div className="col-12 px-2">
                <Inputs
                  label="Deity Tag"
                  type="select"
                  name="tag"
                  placeholder="Select deity"
                  options={tagOptions}
                  value={filters.tag}
                  onChange={(opt) => setFilters((p) => ({ ...p, tag: opt }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Temple Details */}
        <div className="col-12 px-2 mb-4">
          <div className="backgroundpink p-1 rounded-4">
            <h5 className="sub-title p-2 m-0">Temple Details</h5>
            <div className="row p-2 bg-white rounded-bottom-10 g-3">
              <div className="col-md-6 px-2">
                <Inputs
                  label="Religion"
                  type="select"
                  isMultiSelect
                  name="religion"
                  placeholder="Select religion"
                  options={religionOptions}
                  value={filters.religion}
                  onChange={(sel) =>
                    setFilters((p) => ({ ...p, religion: sel || [] }))
                  }
                />
              </div>

              <div className="col-md-6 px-2">
                <Inputs
                  label="Facilities"
                  type="select"
                  isMultiSelect
                  name="facilities"
                  placeholder="Select facilities"
                  options={facilityOptions}
                  value={filters.facilities}
                  onChange={(sel) =>
                    setFilters((p) => ({ ...p, facilities: sel || [] }))
                  }
                />
              </div>

              <div className="col-md-6 px-2">
                <Inputs
                  label="Group of Temples"
                  type="select"
                  isMultiSelect
                  name="group"
                  placeholder="Select group"
                  options={groupOptions}
                  value={filters.group}
                  onChange={(sel) =>
                    setFilters((p) => ({ ...p, group: sel || [] }))
                  }
                />
              </div>

              <div className="col-md-6 px-2">
                <Inputs
                  label="Ownership"
                  type="select"
                  name="ownership"
                  placeholder="Select ownership"
                  options={[
                    { value: "Government", label: "Government" },
                    { value: "Private", label: "Private" },
                    { value: "Trust", label: "Trust" },
                    { value: "Mutt", label: "Mutt" },
                  ]}
                  value={filters.ownership}
                  onChange={(opt) =>
                    setFilters((p) => ({ ...p, ownership: opt }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="col-12 px-2 mb-4">
          <div className="backgroundrose p-1 rounded-4">
            <h5 className="sub-title p-2 m-0">Location</h5>
            <div className="row p-2 bg-white rounded-bottom-10 g-3">
              <div className="col-md-4 px-2">
                <Inputs
                  label="State"
                  type="select"
                  name="state"
                  placeholder="Select state"
                  value={filters.state}
                  options={stateOptions}
                  onChange={(opt) => setFilters((p) => ({ ...p, state: opt }))}
                />
              </div>

              <div className="col-md-4 px-2">
                <Inputs
                  label="District"
                  type="select"
                  name="district"
                  placeholder="Select district"
                  value={filters.district}
                  options={districtOptions}
                  onChange={(opt) =>
                    setFilters((p) => ({ ...p, district: opt }))
                  }
                  disabled={!filters.state}
                />
              </div>

              <div className="col-md-4 px-2">
                <Inputs
                  label="City"
                  type="select"
                  name="city"
                  placeholder="Select city"
                  value={filters.city}
                  options={cityOptions}
                  onChange={(opt) => setFilters((p) => ({ ...p, city: opt }))}
                  disabled={!filters.district}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.footer}>
        <CustomizButton
          type="button"
          label="Clear All"
          icon={<i className="fa-solid fa-xmark"></i>}
          variant="secondary"
          onClick={handleClear}
        />
        <CustomizButton
          type="button"
          label="Apply Filters"
          icon={<i className="fa-solid fa-check"></i>}
          variant="primary"
          onClick={() => {
            const count = countApplied();
            if (onApply) onApply(filters, count);
            onClose();
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default TempleFilterPopup;
